import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import organizationsService from '../../services/crm/organizationsService';
import * as telemarketingService from '../../services/crm/telemarketingService';
import { getErrorMessage, getConfigStatus, TelemarketingConfigStatus, getOrganizationMembers, TeamMember } from '../../services/crm/telemarketingService';
import productsService from '../../services/crm/productsService';
import { toast } from 'react-hot-toast';
import VoiceSelectorModal from '../../components/telemarketing/VoiceSelectorModal';

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const UsersIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const SparklesIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"/>
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const BoxIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const ContactsIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const UploadIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

interface CampaignForm {
  name: string;
  description: string;
  script_template: string;
  objective: string;
  ai_agent_personality: string;
  ai_agent_name: string; // Name the AI introduces itself as
  company_name: string; // Company the AI represents
  value_proposition: string; // Core solution - "We help businesses like yours [value_proposition]"
  max_attempts: number;
  retry_delay_hours: number;
  call_schedule: {
    days: string[];
    hours: { start: string; end: string };
    timezone: string;
  };
  voice_settings: {
    voice_id: string;
    speed: number;
    pitch: number;
  };
  selectedProducts: string[]; // Product IDs (legacy, optional)
  customProductKnowledge: string; // Custom text knowledge for AI
  useExplanatumMethodology: boolean; // Use Explanatum B2B Methodology
  assignToTeamMember: boolean; // Book meetings for specific team member
  assignedUserId?: string; // User ID of assigned sales person
}

const OBJECTIVES = [
  { value: 'book_demo', label: 'Book Demo/Meeting' },
  { value: 'qualify_lead', label: 'Qualify Lead' },
  { value: 'product_launch', label: 'Product Launch Announcement' },
  { value: 'follow_up', label: 'Follow-up Call' },
  { value: 'survey', label: 'Customer Survey' },
  { value: 'renewal', label: 'Contract Renewal' }
];

const PERSONALITIES = [
  { value: 'professional', label: 'Professional', description: 'Formal and business-oriented' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and conversational' },
  { value: 'assertive', label: 'Assertive', description: 'Direct and confident' },
  { value: 'consultative', label: 'Consultative', description: 'Advisory and helpful' }
];

// Explanatum B2B Methodology summary for UI
const EXPLANATUM_METHODOLOGY = {
  name: 'Explanatum B2B Methodology',
  tagline: 'Permission-based, insight-driven sales conversations',
  description: 'AI-powered consultative sales framework combining proven SevenV principles with adaptive intelligence.',
  stages: [
    'Pattern Interrupt',
    'Context & Reason',
    'Qualification',
    'Discovery',
    'Problem Deep Dive',
    'Micro-Approvals',
    'Solution Linking',
    'Objection Handling',
    'Commitment',
    'Wrap Up'
  ],
  keyFeatures: [
    { icon: '🎯', label: 'Micro-Questions', desc: 'Permission-based questions at every stage' },
    { icon: '🛡️', label: 'Objection Handling', desc: '6-step framework: Listen → Validate → Clarify → Isolate → Reframe → Close' },
    { icon: '📊', label: 'Product-Aware', desc: 'Dynamic responses using your product catalog' },
    { icon: '🔥', label: 'Urgency Detection', desc: 'Identifies hot leads and fast-tracks them' },
    { icon: '🎭', label: 'Adaptive Pacing', desc: 'Adjusts style based on prospect responses' },
    { icon: '🚀', label: 'Smart Escalation', desc: 'Transfers to human when appropriate' }
  ],
  builtInObjections: [
    '"Just send me an email"',
    '"Happy with current supplier"',
    '"Too busy / not a good time"',
    '"Too expensive / no budget"',
    '"Need to speak to my boss"',
    '"I don\'t believe it will work"'
  ]
};

// PROVEN SCRIPT TEMPLATES
const SCRIPT_TEMPLATES = [
  {
    id: 'b2b_gatekeeper',
    name: '📞 B2B Gatekeeper + Decision Maker',
    description: 'Professional script designed to bypass gatekeepers and engage decision makers',
    objective: 'book_demo',
    script: `[GATEKEEPER PHASE - If receptionist answers]
"Hi, this is calling for [CONTACT_NAME]. Is {first_name} available?"

[IF ASKED "REGARDING?"]
"It's regarding {company_name}'s [specific area - e.g., sales operations/marketing tech]. It'll just take a minute."

[DECISION MAKER PHASE - When connected]
"Hi {first_name}, thanks for taking my call. I'm calling from [YOUR COMPANY NAME].

I'm reaching out because I noticed {company_name} [SPECIFIC TRIGGER - customize based on research]:
- Recently expanded your team
- Posted a job for [role] suggesting you're scaling [area]
- Might be facing [specific challenge in your industry]

Do you have 90 seconds for me to explain why this could be valuable to you?"

[IF PERMISSION GRANTED]
"Perfect. We help companies like yours [ACHIEVE SPECIFIC OUTCOME] without [CURRENT PAIN POINT].

For example, [CUSTOMER NAME] was facing [similar challenge] and we helped them [specific measurable result] in [timeframe].

Quick question to see if this is relevant: How are you currently handling [SPECIFIC AREA]?"

[OBJECTION HANDLING - Use as needed]
• "Not interested" → "I understand - can I ask what you're currently using for [area]?"
• "Send info" → "Happy to. To send the most relevant info, what's your biggest priority: [option A] or [option B]?"
• "Using competitor" → "Great choice! Out of curiosity, how are they handling [specific gap]?"
• "No budget" → "Understood. Most clients actually save money by switching. When does your fiscal year refresh?"

[CLOSE]
"Based on what you've shared, a 15-minute call with our specialist would be worthwhile. 
I have [DAY] at [TIME] or [DAY] at [TIME] - which works better?"

[IF CALLBACK REQUESTED]
"Perfect - I'll call you back [AGREED TIME]. Just to confirm, this number {phone_number} is best?"

[COMPLIANCE]
*This call may be recorded for quality assurance. If you'd like to opt out of future calls, just let me know.*`
  },
  {
    id: 'follow_up_warm',
    name: '🔄 Follow-Up Call (Warm Lead)',
    description: 'Re-engage prospects who showed initial interest',
    objective: 'book_demo',
    script: `"Hi {first_name}, it's calling from [YOUR COMPANY]. We spoke [TIMEFRAME - e.g., last week] about {company_name}'s [topic discussed].

You mentioned you were [THEIR STATED CHALLENGE/GOAL]. I wanted to follow up because [NEW DEVELOPMENT]:
- We just launched [feature] that directly addresses [their pain point]
- We helped [similar company] achieve [result] and thought of you
- We're running [limited offer] this week

Do you have 2 minutes?"

[IF YES]
"Great! Quick reminder - we help [OUTCOME]. Since we last spoke, here's what's new:

1. [Relevant update #1]
2. [Case study matching their situation]
3. [Special offer if applicable]

You said you wanted to [THEIR GOAL]. Let's get that scheduled. 

I have [DAY] at [TIME] or [DAY] at [TIME] for a proper demo. What works?"

[IF STILL NOT READY]
"No problem - when would be a better time to reconnect? [SCHEDULE SPECIFIC CALLBACK]"

[COMPLIANCE]
*This call is being recorded. If you'd prefer not to receive future calls, please let me know.*`
  },
  {
    id: 'c_level_executive',
    name: '💼 C-Level Executive Approach',
    description: 'High-level value proposition for senior decision makers',
    objective: 'book_demo',
    script: `"Hi {first_name}, it's calling from [YOUR COMPANY]. I'll keep this brief - I know your time is valuable.

I'm calling because we help [TITLE - e.g., CEOs/CFOs/CTOs] at companies like {company_name} [ACHIEVE STRATEGIC OUTCOME]:
- [Benefit #1 with numbers]
- [Benefit #2 with ROI]
- [Benefit #3 with time saved]

[SOCIAL PROOF]
We're working with [COMPETITOR/SIMILAR COMPANY] and [NOTABLE CUSTOMER] - they've seen [specific measurable results].

Is this something you'd want your team to evaluate? If so, I can connect you with our [SPECIALIST] for a focused 15-minute session."

[IF INTERESTED]
"Perfect. What's the best way to get time on your calendar? Email? Or would [DAY/TIME] work?"

[IF DELEGATE]
"Absolutely. Who would be the right person to speak with about [area]? I'll reach out directly."

[IF NOT RIGHT TIME]
"Understood. When should I follow up? [GET SPECIFIC DATE]"

[COMPLIANCE]
*FYI - this call is recorded. If you'd like to opt out, just say the word.*`
  },
  {
    id: 'smb_quick_pitch',
    name: '🏢 SMB Quick Pitch',
    description: 'Fast, benefit-focused approach for small business owners',
    objective: 'book_demo',
    script: `"Hi {first_name}, quick call - do you have 60 seconds?

I help [TYPE OF BUSINESS] owners like you [SOLVE SPECIFIC PROBLEM] so you can [DESIRED OUTCOME].

Quick example: [CUSTOMER NAME] was spending [X hours/£X] on [current process]. 
We helped them cut that by [%] and now they [positive result].

Sound relevant? If so, I can show you how in 10 minutes flat."

[IF YES]
"Perfect! This week I have [DAY] at [TIME] or [DAY] at [TIME]. Which is better?"

[IF "TOO BUSY"]
"I get it - that's exactly why this works. Most clients save [X hours/week]. 
Let me send you a 2-minute video showing how. What's your best email?"

[IF "SEND INFO"]
"Will do. To make it relevant, what's your #1 headache right now: [Option A] or [Option B]?"

[CLOSE]
"Great - I'll send that over and follow up [DAY]. Best number to reach you?"

[COMPLIANCE]
*Just so you know, this call is recorded for training. Want me to remove you from our list? No problem.*`
  },
  {
    id: 'reengagement',
    name: '🔥 Re-engagement (Cold to Warm)',
    description: 'Revive old leads with new angle or trigger event',
    objective: 'qualify_lead',
    script: `"Hi {first_name}, it's calling from [YOUR COMPANY]. We haven't connected in a while.

I'm reaching out because [SPECIFIC TRIGGER EVENT]:
- I saw {company_name} [announcement/hiring/funding/expansion]
- Your industry is dealing with [new regulation/trend]
- We just released [solution to their known problem]

Given that timing, it might be worth reconnecting. Have 60 seconds?"

[IF YES]
"Great. Quick recap - we help [TARGET COMPANIES] [ACHIEVE OUTCOME].

What's changed since we last spoke: [UPDATE #1], [UPDATE #2]

Makes sense to have a fresh conversation about where you're at now?"

[IF INTERESTED]
"Perfect. [DAY] at [TIME] work for a quick 15-minute call?"

[IF NOT INTERESTED]
"No worries. Out of curiosity, what's your current solution for [area]? 
Just want to make sure we're reaching out at the right time."

[COMPLIANCE]
*This call is being recorded. If you want off our contact list, just say so and I'll handle it immediately.*`
  },
  {
    id: 'voicemail',
    name: '📧 Voicemail Script',
    description: 'Leave a concise, compelling message to encourage callback',
    objective: 'callback',
    script: `"Hi {first_name}, this is calling from [YOUR COMPANY]. 

Quick message: I'm calling about [SPECIFIC TRIGGER/REASON].

We've helped companies like [COMPETITOR or SIMILAR COMPANY] [achieve specific result] and I think we could do the same for {company_name}.

Worth a 10-minute conversation? Call me back at [YOUR DEDICATED NUMBER] or I'll try you again [DAY/TIME].

Again, that's [YOUR NAME] from [COMPANY] at [NUMBER]. Thanks!"

[HANG UP - Keep under 30 seconds total]`
  }
];

const DAYS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' }
];

export default function CreateTelemarketingCampaign() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeOrgId, setActiveOrgId, isBootstrapped, setBootstrapped } = useCrmOrgStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(!isBootstrapped);

  // Bootstrap organization on mount if not already done
  useEffect(() => {
    const bootstrap = async () => {
      if (!user?.id) return;
      if (activeOrgId && isBootstrapped) {
        setBootstrapping(false);
        return;
      }

      try {
        const response = await organizationsService.list();
        const list = response.data || [];

        if (list.length === 0) {
          // Create default organization
          const created = await organizationsService.create('My Organization');
          if (created.data) {
            setActiveOrgId(created.data.id);
          }
        } else if (!activeOrgId) {
          // Use first organization
          setActiveOrgId(list[0].id);
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organization:', err);
        toast.error('Failed to load organization');
      } finally {
        setBootstrapped(true);
        setBootstrapping(false);
      }
    };

    bootstrap();
  }, [user?.id, activeOrgId, isBootstrapped, setActiveOrgId, setBootstrapped]);

  const [form, setForm] = useState<CampaignForm>({
    name: '',
    description: '',
    script_template: '',
    objective: 'book_demo',
    ai_agent_personality: 'professional',
    ai_agent_name: '',
    company_name: '',
    value_proposition: '', // Core solution for opening pitch
    max_attempts: 3,
    retry_delay_hours: 24,
    call_schedule: {
      days: ['mon', 'tue', 'wed', 'thu', 'fri'],
      hours: { start: '09:00', end: '17:00' },
      timezone: 'America/New_York'
    },
    voice_settings: {
      voice_id: 'Xb7hH8MSUJpSbSDYk0k2',
      speed: 1.0,
      pitch: 1.0
    },
    selectedProducts: [],
    customProductKnowledge: '', // Custom text for product/service knowledge
    useExplanatumMethodology: true, // Default to using the methodology
    assignToTeamMember: false, // Default to current user's calendar
    assignedUserId: undefined
  });

  const [products, setProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showScriptTemplates, setShowScriptTemplates] = useState(false);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [selectedVoiceName, setSelectedVoiceName] = useState('Default Voice');
  const [configStatus, setConfigStatus] = useState<TelemarketingConfigStatus | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  
  // Contacts state for Step 3
  const [contactsText, setContactsText] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedContacts, setParsedContacts] = useState<Array<{ name: string; phone: string; company?: string; email?: string }>>([]);
  const [contactsError, setContactsError] = useState<string | null>(null);

  // Load config status, products, and team members on mount
  useEffect(() => {
    if (activeOrgId) {
      loadProducts();
      loadConfigStatus();
      loadTeamMembers();
    }
  }, [activeOrgId]);

  const loadTeamMembers = async () => {
    if (!activeOrgId) return;
    setLoadingTeamMembers(true);
    try {
      const members = await getOrganizationMembers(activeOrgId);
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to load team members:', error);
      // Don't show error toast - team members are optional
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const loadConfigStatus = async () => {
    try {
      setConfigLoading(true);
      const status = await getConfigStatus();
      setConfigStatus(status);
    } catch (error) {
      console.error('Failed to load config status:', error);
      // Don't block the UI, just log the error
    } finally {
      setConfigLoading(false);
    }
  };

  const loadProducts = async () => {
    if (!activeOrgId) return;
    setLoadingProducts(true);
    try {
      const data = await productsService.list(activeOrgId);
      setProducts(data.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug: Log all form values to understand state
    console.log('=== LAUNCH CAMPAIGN DEBUG ===');
    console.log('activeOrgId:', activeOrgId);
    console.log('form.name:', JSON.stringify(form.name));
    console.log('form.ai_agent_name:', JSON.stringify(form.ai_agent_name));
    console.log('form.company_name:', JSON.stringify(form.company_name));
    console.log('form.value_proposition:', JSON.stringify(form.value_proposition));
    console.log('parsedContacts.length:', parsedContacts.length);
    console.log('Full form state:', JSON.stringify(form, null, 2));
    
    if (!activeOrgId) {
      console.log('ERROR: No activeOrgId');
      toast.error('No organization selected. Please refresh and try again.');
      return;
    }

    // Collect ALL validation errors with their step numbers
    const errors: Array<{ field: string; message: string; step: number }> = [];
    const warnings: string[] = [];
    
    // Step 1: Basic Info validation - use String() to handle undefined
    const nameValue = String(form.name || '').trim();
    const agentNameValue = String(form.ai_agent_name || '').trim();
    const companyNameValue = String(form.company_name || '').trim();
    const valuePropValue = String(form.value_proposition || '').trim();
    
    if (!nameValue) {
      errors.push({ field: 'Campaign Name', message: 'Campaign name is required', step: 1 });
    }
    if (!agentNameValue) {
      errors.push({ field: 'Agent Name', message: 'Agent name is required - the AI needs a name to introduce itself', step: 1 });
    }
    if (!companyNameValue) {
      errors.push({ field: 'Company Name', message: 'Company name is required', step: 1 });
    }
    if (!valuePropValue) {
      errors.push({ field: 'Value Proposition', message: 'Value proposition is required for quality calls', step: 1 });
    }
    
    console.log('Validation errors:', errors);

    // Step 5: Schedule validation
    if (!form.call_schedule?.days || form.call_schedule.days.length === 0) {
      warnings.push('No calling days selected (Step 5) - campaign won\'t make calls');
    }

    // Step 2: Knowledge warnings (not required but recommended)
    if (form.selectedProducts.length === 0 && !form.customProductKnowledge?.trim()) {
      warnings.push('No product knowledge (Step 2) - add custom knowledge or link CRM products for better calls');
    }
    
    // Step 4: Script warnings (only if not using methodology)
    if (!form.useExplanatumMethodology && !form.script_template?.trim()) {
      warnings.push('No script template (Step 4) - calls may lack clear messaging');
    }

    // If there are required field errors, show them all and go to the first problematic step
    if (errors.length > 0) {
      const firstErrorStep = errors[0].step;
      const fieldList = errors.map(e => `${e.field} (Step ${e.step})`).join(', ');
      
      toast.error(`Missing required fields: ${fieldList}`, { duration: 6000 });
      
      // Navigate to the step with the first error
      setCurrentStep(firstErrorStep);
      return;
    }

    // Show warnings but allow user to proceed
    if (warnings.length > 0) {
      const proceed = window.confirm(
        `Campaign has the following warnings:\n\n${warnings.join('\n')}\n\nDo you want to continue anyway?`
      );
      if (!proceed) return;
    }

    setSaving(true);
    try {
      // Build target_criteria with selected products and custom knowledge
      const selectedProductDetails = products.filter(p => form.selectedProducts.includes(p.id));
      
      const payload = {
        organization_id: activeOrgId,
        name: form.name,
        description: form.description,
        script_template: form.script_template,
        objective: form.objective,
        ai_agent_personality: form.ai_agent_personality,
        ai_agent_name: form.ai_agent_name,
        company_name: form.company_name,
        max_attempts: form.max_attempts,
        retry_delay_hours: form.retry_delay_hours,
        call_schedule: form.call_schedule,
        voice_settings: form.voice_settings,
        target_criteria: {
          products: selectedProductDetails.map(p => ({
            id: p.id,
            name: p.name,
            description: p.description,
            price: p.default_price,
            sku: p.sku,
            category: p.category
          })),
          // Custom product/service knowledge for AI - THIS IS THE MAIN KNOWLEDGE SOURCE
          custom_knowledge: form.customProductKnowledge || null
        },
        // Value proposition for opening pitch - critical for call quality
        value_proposition: form.value_proposition,
        use_explanatum_methodology: form.useExplanatumMethodology,
        methodology_config: form.useExplanatumMethodology ? {
          name: 'Explanatum_B2B_Methodology',
          version: '1.0.0',
          agent_name: form.ai_agent_name,
          company_name: form.company_name,
          value_proposition: form.value_proposition, // Core solution for opening pitch
          enabled_stages: EXPLANATUM_METHODOLOGY.stages.map(s => s.toLowerCase().replace(/ /g, '_'))
        } : null,
        // Team member assignment for meeting booking
        assigned_user_id: form.assignToTeamMember && form.assignedUserId ? form.assignedUserId : null,
        // Set to active if we have contacts, otherwise draft
        status: parsedContacts.length > 0 ? 'active' : 'draft'
      };

      console.log('Creating campaign with payload:', payload);
      const campaign = await telemarketingService.createCampaign(payload as any);
      console.log('Campaign created:', campaign);
      
      if (!campaign?.id) {
        console.error('Campaign created but no ID returned:', campaign);
        toast.success('Campaign created! Returning to campaigns list.');
        navigate('/crm/telemarketing');
        return;
      }
      
      // Add contacts if any were entered
      if (parsedContacts.length > 0) {
        try {
          await telemarketingService.addCampaignTargets(campaign.id, activeOrgId, parsedContacts);
          toast.success(`Campaign launched with ${parsedContacts.length} contacts! Starting calls...`);
        } catch (contactError) {
          console.error('Failed to add contacts:', contactError);
          toast.success('Campaign created! (Some contacts failed to import)');
        }
      } else {
        toast.success('Campaign created as draft. Add contacts to start calling.');
      }
      
      // Navigate to campaign call monitor to manage/start calls
      console.log('Navigating to campaign calls:', campaign.id);
      navigate(`/crm/telemarketing/${campaign.id}/calls`);
    } catch (error: any) {
      console.error('Failed to create campaign:', error);
      
      // Use centralized error message mapping
      const friendlyMessage = getErrorMessage(error);
      toast.error(friendlyMessage);
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { num: 1, title: 'Basic Info', icon: DocumentIcon },
    { num: 2, title: 'Knowledge', icon: SparklesIcon },
    { num: 3, title: 'Contacts', icon: ContactsIcon },
    { num: 4, title: 'Script & AI', icon: DocumentIcon },
    { num: 5, title: 'Schedule', icon: ClockIcon },
    { num: 6, title: 'Settings', icon: UsersIcon }
  ];

  const toggleProduct = (productId: string) => {
    setForm(prev => ({
      ...prev,
      selectedProducts: prev.selectedProducts.includes(productId)
        ? prev.selectedProducts.filter(id => id !== productId)
        : [...prev.selectedProducts, productId]
    }));
  };

  const selectTemplate = (template: typeof SCRIPT_TEMPLATES[0]) => {
    setForm(prev => ({
      ...prev,
      script_template: template.script,
      objective: template.objective
    }));
    setShowScriptTemplates(false);
    toast.success(`Template loaded: ${template.name}`);
  };

  const handleVoiceSelect = (voiceId: string, voiceName: string) => {
    setForm(prev => ({
      ...prev,
      voice_settings: {
        ...prev.voice_settings,
        voice_id: voiceId
      }
    }));
    setSelectedVoiceName(voiceName);
  };

  // Show loading while bootstrapping organization
  if (bootstrapping) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading organization...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Create AI Telemarketing Campaign</h1>
        <p className="text-gray-600 mt-1">Set up an automated cold calling campaign</p>
      </div>

      {/* Configuration Status Banner */}
      {!configLoading && configStatus && !configStatus.ready && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="text-2xl">⚠️</div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">Telemarketing Setup Incomplete</h3>
              <p className="text-sm text-amber-700 mt-1">
                Some services are not configured. Campaigns may not work until these are resolved:
              </p>
              <ul className="mt-2 space-y-1">
                {!configStatus.aiConfigured && (
                  <li className="text-sm text-amber-700 flex items-center gap-2">
                    <span className="text-red-500">✗</span> AI Service: {configStatus.aiError || 'Not configured'}
                  </li>
                )}
                {!configStatus.twilioConfigured && (
                  <li className="text-sm text-amber-700 flex items-center gap-2">
                    <span className="text-red-500">✗</span> Calling (Twilio): {configStatus.twilioError || 'Not configured'}
                  </li>
                )}
                {!configStatus.ttsConfigured && (
                  <li className="text-sm text-amber-700 flex items-center gap-2">
                    <span className="text-red-500">✗</span> Voice (ElevenLabs): {configStatus.ttsError || 'Not configured'}
                  </li>
                )}
              </ul>
              <p className="text-xs text-amber-600 mt-2">
                Contact your administrator to complete the setup.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    currentStep >= step.num
                      ? 'bg-purple-600 border-purple-600 text-white'
                      : 'bg-white border-gray-300 text-gray-400'
                  }`}
                >
                  {currentStep > step.num ? (
                    <CheckIcon className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${currentStep >= step.num ? 'text-gray-900' : 'text-gray-400'}`}>
                    {step.title}
                  </p>
                </div>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${currentStep > step.num ? 'bg-purple-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
        {/* Step 1: Basic Info */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., Q1 Lead Generation Campaign"
                required
              />
            </div>

            {/* AI Agent Identity Section */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                  <UsersIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI Agent Identity</h3>
                  <p className="text-xs text-gray-600">How will the AI introduce itself on calls?</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent Name *
                    <span className="ml-1 text-xs text-gray-500 font-normal">(First name the AI will use)</span>
                  </label>
                  <input
                    type="text"
                    value={form.ai_agent_name}
                    onChange={(e) => setForm(prev => ({ ...prev, ai_agent_name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Sarah, Michael, Alex"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                    <span className="ml-1 text-xs text-gray-500 font-normal">(Your business name)</span>
                  </label>
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setForm(prev => ({ ...prev, company_name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Acme Solutions"
                    required
                  />
                </div>
              </div>

              {/* Value Proposition - CRITICAL for opening pitch */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4 mt-4">
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Core Value Proposition *
                  <span className="ml-1 text-xs text-amber-700 font-normal">(Critical for opening pitch quality)</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Complete this sentence: <strong>"We help businesses like yours..."</strong>
                </p>
                <input
                  type="text"
                  value={form.value_proposition}
                  onChange={(e) => setForm(prev => ({ ...prev, value_proposition: e.target.value }))}
                  className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                  placeholder="e.g., reduce operational costs by 30% through automation"
                  required
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500">Examples:</span>
                  {[
                    'streamline their sales process and close more deals',
                    'reduce admin time by 50% with smart automation',
                    'generate qualified leads on autopilot',
                    'scale customer support without adding headcount'
                  ].map((example, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, value_proposition: example }))}
                      className="text-xs px-2 py-1 bg-white border border-amber-200 rounded hover:bg-amber-100 transition-colors"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Live Preview of Opening Pitch */}
              {form.ai_agent_name && form.company_name && (
                <div className="mt-4 p-4 bg-white rounded-lg border-2 border-purple-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">🎯</span>
                    <span className="font-semibold text-purple-600 text-sm">Live Opening Pitch Preview</span>
                  </div>
                  <p className="text-gray-800 leading-relaxed">
                    "Hi, this is{' '}
                    <span className="font-semibold text-purple-700">{form.ai_agent_name}</span> calling from{' '}
                    <span className="font-semibold text-purple-700">{form.company_name}</span>. We help businesses similar to yours{' '}
                    <span className={`font-semibold ${form.value_proposition ? 'text-amber-600' : 'text-red-400 italic'}`}>
                      {form.value_proposition || '[enter value proposition above]'}
                    </span>
                    . Would you have a moment to discuss your experiences?"
                  </p>
                  {!form.value_proposition && (
                    <p className="text-xs text-red-500 mt-2">⚠️ Value proposition is required for high-quality calls</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Brief description of campaign goals..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Objective *</label>
              <select
                value={form.objective}
                onChange={(e) => setForm(prev => ({ ...prev, objective: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {OBJECTIVES.map((obj) => (
                  <option key={obj.value} value={obj.value}>
                    {obj.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Product Knowledge */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Custom Product Knowledge - PRIMARY */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-600 rounded-lg">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Product & Service Knowledge</h3>
                  <p className="text-sm text-gray-600">
                    This is the main knowledge source for the AI. Describe your products, services, pricing, features, and benefits.
                  </p>
                </div>
              </div>

              <textarea
                value={form.customProductKnowledge}
                onChange={(e) => setForm(prev => ({ ...prev, customProductKnowledge: e.target.value }))}
                rows={12}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                placeholder={`Describe your products and services in detail. The AI will use this knowledge during calls.

Example:
---
PRODUCT: Enterprise CRM Suite
PRICE: £299/month (3 users included), £49/user/month additional
FEATURES:
- Contact management with unlimited storage
- Email integration (Gmail, Outlook)
- Sales pipeline with custom stages
- Automated follow-up reminders
- Real-time analytics dashboard

KEY BENEFITS:
- Save 10+ hours per week on admin tasks
- Increase close rate by 30% with automated follow-ups
- Single source of truth for all customer data

COMMON OBJECTIONS & RESPONSES:
- "Too expensive" → We typically help teams save £1,000+/month in lost productivity
- "Already have a CRM" → Most clients migrate in under a week, and our team handles the data transfer
- "Need to think about it" → What specific concerns would you like me to address?

COMPETITORS:
- Salesforce: We're 60% cheaper with dedicated UK support
- HubSpot: We include features they charge extra for
---`}
              />

              {/* PDF Upload */}
              <div className="mt-4 flex items-center gap-4">
                <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <DocumentIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {uploadingPdf ? 'Processing...' : 'Upload PDF'}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.txt,.doc,.docx"
                    className="hidden"
                    disabled={uploadingPdf}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setUploadingPdf(true);
                      try {
                        // For PDF, we'll extract text client-side
                        if (file.type === 'application/pdf') {
                          // Use pdf.js or similar to extract text
                          toast.error('PDF extraction coming soon. Please paste text directly for now.');
                        } else if (file.type === 'text/plain') {
                          const text = await file.text();
                          setForm(prev => ({
                            ...prev,
                            customProductKnowledge: prev.customProductKnowledge 
                              ? prev.customProductKnowledge + '\n\n---\n\n' + text 
                              : text
                          }));
                          setPdfFileName(file.name);
                          toast.success(`Added content from ${file.name}`);
                        } else {
                          toast.error('Please upload a .txt or .pdf file');
                        }
                      } catch (err) {
                        console.error('Failed to read file:', err);
                        toast.error('Failed to read file');
                      } finally {
                        setUploadingPdf(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
                {pdfFileName && (
                  <span className="text-sm text-green-600">✓ {pdfFileName} added</span>
                )}
                <span className="text-sm text-gray-500">
                  Supports .txt files (PDF extraction coming soon)
                </span>
              </div>

              {/* Character count */}
              <div className="mt-2 flex justify-between text-xs text-gray-500">
                <span>
                  {form.customProductKnowledge.length > 0 
                    ? `${form.customProductKnowledge.length.toLocaleString()} characters`
                    : 'No knowledge added yet'}
                </span>
                <span>Recommended: 500-5,000 characters</span>
              </div>
            </div>

            {/* CRM Products - OPTIONAL/LEGACY */}
            <div className="border border-gray-200 rounded-xl p-4">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <BoxIcon className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-700">
                      Link CRM Products (Optional)
                    </span>
                    {form.selectedProducts.length > 0 && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                        {form.selectedProducts.length} selected
                      </span>
                    )}
                  </div>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600 mb-4">
                    Optionally link CRM products for basic pricing reference. For detailed knowledge, use the text field above.
                  </p>

                  {loadingProducts ? (
                    <div className="text-center py-4 text-gray-500">Loading products...</div>
                  ) : products.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">
                        No CRM products found. <a href="/crm/products" className="text-purple-600 underline">Add products</a> or use the custom knowledge field above.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {products.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => toggleProduct(product.id)}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${
                            form.selectedProducts.includes(product.id)
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 text-sm truncate">{product.name}</h4>
                              {product.default_price && (
                                <p className="text-xs text-purple-600">£{product.default_price}</p>
                              )}
                            </div>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ml-2 flex-shrink-0 ${
                              form.selectedProducts.includes(product.id)
                                ? 'bg-purple-600 border-purple-600'
                                : 'border-gray-300'
                            }`}>
                              {form.selectedProducts.includes(product.id) && (
                                <CheckIcon className="w-2.5 h-2.5 text-white" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            </div>

            {/* Status indicator */}
            {(form.customProductKnowledge?.trim() || form.selectedProducts.length > 0) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium">
                  ✓ Knowledge configured! The AI will use this information during calls.
                </p>
                <ul className="mt-2 text-sm text-green-700 space-y-1">
                  {form.customProductKnowledge?.trim() && (
                    <li>• Custom knowledge: {form.customProductKnowledge.length.toLocaleString()} characters</li>
                  )}
                  {form.selectedProducts.length > 0 && (
                    <li>• CRM Products: {form.selectedProducts.length} linked</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Contacts - Add people to call */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ContactsIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Add Contacts to Call</h3>
                  <p className="text-sm text-gray-600">Enter phone numbers and names for your campaign</p>
                </div>
              </div>

              {/* Simple text input for contacts */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Contacts (one per line)
                </label>
                <textarea
                  value={contactsText}
                  onChange={(e) => {
                    setContactsText(e.target.value);
                    setContactsError(null);
                    // Parse contacts as user types
                    const lines = e.target.value.split('\n').filter(l => l.trim());
                    const contacts = lines.map(line => {
                      // Try to parse: Name, Phone, Company (optional)
                      const parts = line.split(',').map(p => p.trim());
                      if (parts.length >= 2) {
                        return {
                          name: parts[0],
                          phone: parts[1],
                          company: parts[2] || undefined,
                          email: parts[3] || undefined
                        };
                      } else if (parts.length === 1 && /[\d\s\+\-\(\)]+/.test(parts[0])) {
                        // Just a phone number
                        return { name: 'Unknown', phone: parts[0] };
                      }
                      return null;
                    }).filter(Boolean) as Array<{ name: string; phone: string; company?: string; email?: string }>;
                    setParsedContacts(contacts);
                  }}
                  rows={8}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder={`Format: Name, Phone, Company (optional), Email (optional)

Examples:
John Smith, +44 7911 123456, Acme Corp
Sarah Johnson, 020 7123 4567, Example Ltd, sarah@example.com
David Williams, +1 555 123 4567

Or just phone numbers:
+44 7911 123456
020 7123 4567`}
                />
              </div>

              {/* Parsed contacts preview */}
              {parsedContacts.length > 0 && (
                <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
                  <h4 className="font-medium text-gray-900 mb-2">
                    ✓ {parsedContacts.length} contact{parsedContacts.length !== 1 ? 's' : ''} ready
                  </h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {parsedContacts.slice(0, 10).map((c, i) => (
                      <div key={i} className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="text-blue-500">•</span>
                        <span className="font-medium">{c.name}</span>
                        <span className="text-gray-400">-</span>
                        <span>{c.phone}</span>
                        {c.company && <span className="text-gray-400">({c.company})</span>}
                      </div>
                    ))}
                    {parsedContacts.length > 10 && (
                      <div className="text-sm text-gray-500 italic">
                        +{parsedContacts.length - 10} more contacts...
                      </div>
                    )}
                  </div>
                </div>
              )}

              {contactsError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {contactsError}
                </div>
              )}

              {/* CSV Upload Option */}
              <div className="border-t border-blue-200 pt-4 mt-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                    <UploadIcon className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Upload CSV</span>
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        try {
                          const text = await file.text();
                          setContactsText(text);
                          setCsvFile(file);
                          
                          // Parse CSV
                          const lines = text.split('\n').filter(l => l.trim());
                          const hasHeader = lines[0]?.toLowerCase().includes('name') || lines[0]?.toLowerCase().includes('phone');
                          const dataLines = hasHeader ? lines.slice(1) : lines;
                          
                          const contacts = dataLines.map(line => {
                            const parts = line.split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
                            if (parts.length >= 2) {
                              return {
                                name: parts[0] || parts[1],
                                phone: parts[1] || parts[0],
                                company: parts[2] || undefined,
                                email: parts[3] || undefined
                              };
                            }
                            return null;
                          }).filter(Boolean) as Array<{ name: string; phone: string; company?: string; email?: string }>;
                          
                          setParsedContacts(contacts);
                          toast.success(`Loaded ${contacts.length} contacts from ${file.name}`);
                        } catch (err) {
                          console.error('Failed to parse CSV:', err);
                          toast.error('Failed to parse CSV file');
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {csvFile && (
                    <span className="text-sm text-green-600">✓ {csvFile.name}</span>
                  )}
                  <a
                    href="/api/telemarketing/csv-template"
                    download
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Download CSV template
                  </a>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  CSV format: first_name, last_name, phone, email (optional), company (optional)
                </p>
              </div>
            </div>

            {/* Info box about adding contacts later */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> You can also add or import more contacts after creating the campaign from the campaign management page.
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Script & AI */}
        {currentStep === 4 && (
          <div className="space-y-6">
            {/* Explanatum B2B Methodology Section */}
            <div className="border-2 border-purple-200 rounded-xl overflow-hidden">
              <div 
                className={`p-4 cursor-pointer transition-all ${
                  form.useExplanatumMethodology 
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                onClick={() => setForm(prev => ({ ...prev, useExplanatumMethodology: !prev.useExplanatumMethodology }))}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      form.useExplanatumMethodology 
                        ? 'bg-white border-white' 
                        : 'border-gray-400'
                    }`}>
                      {form.useExplanatumMethodology && (
                        <CheckIcon className="w-4 h-4 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <h3 className={`font-bold text-lg ${form.useExplanatumMethodology ? 'text-white' : 'text-gray-900'}`}>
                        {EXPLANATUM_METHODOLOGY.name}
                      </h3>
                      <p className={`text-sm ${form.useExplanatumMethodology ? 'text-purple-100' : 'text-gray-600'}`}>
                        {EXPLANATUM_METHODOLOGY.tagline}
                      </p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    form.useExplanatumMethodology 
                      ? 'bg-green-400 text-green-900' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {form.useExplanatumMethodology ? '✓ ENABLED' : 'DISABLED'}
                  </div>
                </div>
              </div>
              
              {form.useExplanatumMethodology && (
                <div className="p-4 bg-purple-50 border-t border-purple-200">
                  <p className="text-sm text-gray-700 mb-4">{EXPLANATUM_METHODOLOGY.description}</p>
                  
                  {/* Key Features Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    {EXPLANATUM_METHODOLOGY.keyFeatures.map((feature, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-purple-100">
                        <div className="text-lg mb-1">{feature.icon}</div>
                        <div className="font-semibold text-sm text-gray-900">{feature.label}</div>
                        <div className="text-xs text-gray-600">{feature.desc}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Built-in Objections */}
                  <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <div className="font-semibold text-sm text-gray-900 mb-2">Built-in Objection Handlers:</div>
                    <div className="flex flex-wrap gap-2">
                      {EXPLANATUM_METHODOLOGY.builtInObjections.map((objection, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                          {objection}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Call Flow */}
                  <div className="mt-4">
                    <div className="font-semibold text-sm text-gray-900 mb-2">Intelligent Call Flow:</div>
                    <div className="flex flex-wrap items-center gap-1 text-xs">
                      {EXPLANATUM_METHODOLOGY.stages.map((stage, idx) => (
                        <React.Fragment key={idx}>
                          <span className="px-2 py-1 bg-purple-600 text-white rounded">{stage}</span>
                          {idx < EXPLANATUM_METHODOLOGY.stages.length - 1 && (
                            <span className="text-purple-400">→</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Only show manual script input when Explanatum methodology is disabled */}
            {!form.useExplanatumMethodology && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Call Script Template *</label>
                  <button
                    type="button"
                    onClick={() => setShowScriptTemplates(true)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <SparklesIcon className="w-4 h-4 mr-1.5" />
                    Example Scripts
                  </button>
                </div>
                <textarea
                  value={form.script_template}
                  onChange={(e) => setForm(prev => ({ ...prev, script_template: e.target.value }))}
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                  placeholder="Click 'Example Scripts' to load a proven template, or write your own script here..."
                  required
                />
                <div className="mt-2 flex items-start space-x-4">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">
                      Use placeholders: {'{first_name}'}, {'{company_name}'}, {'{job_title}'} for personalization
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded px-3 py-1">
                    <p className="text-xs text-blue-700 font-medium">
                      💡 Include gatekeeper handling + objections
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">AI Agent Personality</label>
              <div className="grid grid-cols-2 gap-4">
                {PERSONALITIES.map((personality) => (
                  <button
                    key={personality.value}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, ai_agent_personality: personality.value }))}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      form.ai_agent_personality === personality.value
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{personality.label}</div>
                    <div className="text-sm text-gray-500 mt-1">{personality.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Voice Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">AI Voice</label>
              <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{selectedVoiceName}</div>
                        <div className="text-sm text-gray-500">
                          ElevenLabs AI Voice • Professional Quality
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVoiceSelector(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
                  >
                    Choose Voice
                  </button>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    💡 <strong>Tip:</strong> Professional, clear voices with neutral accents work best for telemarketing. 
                    Preview voices to find the perfect match for your brand.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Schedule */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Calling Days</label>
              <div className="grid grid-cols-4 gap-3">
                {DAYS.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => {
                      const days = form.call_schedule.days.includes(day.value)
                        ? form.call_schedule.days.filter((d) => d !== day.value)
                        : [...form.call_schedule.days, day.value];
                      setForm(prev => ({
                        ...prev,
                        call_schedule: { ...prev.call_schedule, days }
                      }));
                    }}
                    className={`py-2 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                      form.call_schedule.days.includes(day.value)
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                <input
                  type="time"
                  value={form.call_schedule.hours.start}
                  onChange={(e) =>
                    setForm(prev => ({
                      ...prev,
                      call_schedule: {
                        ...prev.call_schedule,
                        hours: { ...prev.call_schedule.hours, start: e.target.value }
                      }
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                <input
                  type="time"
                  value={form.call_schedule.hours.end}
                  onChange={(e) =>
                    setForm(prev => ({
                      ...prev,
                      call_schedule: {
                        ...prev.call_schedule,
                        hours: { ...prev.call_schedule.hours, end: e.target.value }
                      }
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              <select
                value={form.call_schedule.timezone}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    call_schedule: { ...prev.call_schedule, timezone: e.target.value }
                  }))
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 6: Settings */}
        {currentStep === 6 && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Attempts per Contact</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={form.max_attempts}
                  onChange={(e) => setForm(prev => ({ ...prev, max_attempts: parseInt(e.target.value) }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Retry Delay (hours)</label>
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={form.retry_delay_hours}
                  onChange={(e) => setForm(prev => ({ ...prev, retry_delay_hours: parseInt(e.target.value) }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Meeting Assignment Section */}
            <div className="border-2 border-gray-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex items-center h-6">
                  <input
                    type="checkbox"
                    id="assignToTeamMember"
                    checked={form.assignToTeamMember}
                    onChange={(e) => setForm(prev => ({ 
                      ...prev, 
                      assignToTeamMember: e.target.checked,
                      assignedUserId: e.target.checked ? prev.assignedUserId : undefined
                    }))}
                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="assignToTeamMember" className="block text-sm font-semibold text-gray-900 cursor-pointer">
                    Book meetings for a specific team member
                  </label>
                  <p className="text-sm text-gray-500 mt-1">
                    When enabled, booked meetings will be scheduled on the selected team member's calendar instead of yours.
                  </p>
                </div>
              </div>

              {form.assignToTeamMember && (
                <div className="mt-4 pl-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign meetings to:
                  </label>
                  {loadingTeamMembers ? (
                    <div className="text-sm text-gray-500">Loading team members...</div>
                  ) : teamMembers.length === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        No team members found. <a href="/team" className="font-semibold underline">Invite team members</a> to assign campaigns to them.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={form.assignedUserId || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, assignedUserId: e.target.value || undefined }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select a team member...</option>
                      {teamMembers.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.full_name || member.email} {member.role === 'owner' ? '(Owner)' : member.role === 'admin' ? '(Admin)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {form.assignedUserId && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        ✓ Meetings booked by this campaign will appear on {teamMembers.find(m => m.user_id === form.assignedUserId)?.full_name || 'the selected member'}'s calendar.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <PhoneIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Ready to Launch</p>
                  <p>Your campaign will be created in draft mode. You can add target contacts and start calling from the campaign dashboard.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => {
              if (currentStep === 1) {
                navigate('/crm/telemarketing');
              } else {
                setCurrentStep(currentStep - 1);
              }
            }}
            className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>

          <div className="flex gap-3">
            {currentStep < 6 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
              >
                Next Step
              </button>
            ) : (
              <button
                type="button"
                disabled={saving}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e as any);
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
              >
                {saving ? 'Launching...' : (parsedContacts.length > 0 ? '🚀 Launch Campaign' : 'Create Draft')}
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Example Scripts Modal */}
      {showScriptTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Proven Script Templates</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Professional scripts designed for high conversion rates. Click to load and customize.
                  </p>
                </div>
                <button
                  onClick={() => setShowScriptTemplates(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {SCRIPT_TEMPLATES.map((template) => (
                  <div
                    key={template.id}
                    className="border-2 border-gray-200 rounded-lg hover:border-purple-400 transition-all cursor-pointer group"
                    onClick={() => selectTemplate(template)}
                  >
                    <div className="p-4 border-b border-gray-200 bg-gray-50 group-hover:bg-purple-50 transition-colors">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-purple-700">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                    </div>
                    <div className="p-4">
                      <div className="bg-gray-50 rounded p-3 border border-gray-200">
                        <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap line-clamp-6 leading-relaxed">
                          {template.script.substring(0, 300)}...
                        </pre>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          selectTemplate(template);
                        }}
                        className="mt-3 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors group-hover:bg-purple-700"
                      >
                        Use This Template
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Key Features Note */}
              <div className="mt-8 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
                <h4 className="font-bold text-gray-900 mb-3">✨ What Makes These Scripts Effective:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>Gatekeeper handling:</strong> Scripts to bypass receptionists professionally</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>Permission patterns:</strong> Get prospect's buy-in before pitching</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>Objection handling:</strong> Pre-built responses to common pushback</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>Value-first approach:</strong> Focus on benefits, not features</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>Compliance built-in:</strong> Recording disclosure and opt-out handling</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>Clear call-to-action:</strong> Specific next steps to close the call</span>
                  </div>
                </div>
                <p className="mt-4 text-xs text-gray-600 italic">
                  💡 Tip: Customize the template with your company name, products, and specific value propositions before launching your campaign.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowScriptTemplates(false)}
                className="w-full px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Selector Modal */}
      <VoiceSelectorModal
        isOpen={showVoiceSelector}
        onClose={() => setShowVoiceSelector(false)}
        currentVoiceId={form.voice_settings.voice_id}
        onSelectVoice={handleVoiceSelect}
      />
    </div>
  );
}
